import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MODAL_TYPES } from '@components/modals/constants';
import { useBillingEntities } from '@features/billing-entities';
import type { EligibilityCheckResponse } from '@features/patient-coverages';
import { getFinancials } from '@features/patient-coverages';
import { useModal } from '@store/hooks';

import type { BlockReadiness } from '../types/readiness';

export interface ReadinessActionItem {
  label: string;
  onClick: () => void;
}

export interface ReadinessAction {
  primary?: ReadinessActionItem;
  secondary?: ReadinessActionItem;
}

interface Options {
  readiness: BlockReadiness | null;
  hasCoverage: boolean;
  eligibilityData: EligibilityCheckResponse | undefined;
  patientId?: string;
  encounterId?: string;
  patientName?: string;
}

/**
 * Maps a readiness state to its primary/secondary action for the popover and
 * inline summary.
 *
 *   collect       → Take payment (PAYMENT_REQUEST modal). If billing NPI is
 *                   missing on the default entity, link to billing settings
 *                   instead so the user can finish setup before collecting.
 *                   Encounter scope falls back to `pinned_encounter_id` from
 *                   the BE response when no encounterId prop was passed —
 *                   keeps the copay charge associated with the right visit.
 *   blocked       → Add coverage (INSURANCE_EDIT modal)
 *   !has_coverage → Add coverage
 *   verified /
 *   collected /
 *   attention /
 *   info /
 *   null          → No action — surface is informational.
 */
export function useReadinessAction({
  readiness,
  hasCoverage,
  patientId,
  encounterId,
  patientName,
  eligibilityData,
}: Options): ReadinessAction | null {
  const navigate = useNavigate();
  const { open: openModal } = useModal();
  const { entities: billingEntities } = useBillingEntities({});

  const missingBillingNpi = !billingEntities.some(
    (e) => e.is_default && !!e.billing_npi,
  );

  const openAddCoverage = useCallback(() => {
    if (!patientId) return;
    openModal(MODAL_TYPES.INSURANCE_EDIT, { patientId });
  }, [openModal, patientId]);

  const openTakePayment = useCallback(() => {
    if (missingBillingNpi) {
      navigate('/practice/billing-settings');
      return;
    }
    const fin = getFinancials(eligibilityData);
    const due = fin.member_obligation;
    const amount = due != null ? due.toFixed(2) : undefined;
    // Trust the BE's pinned encounter when the caller didn't pass one — the
    // server has already worked out which visit this charge belongs to.
    const resolvedEncounterId = encounterId ?? eligibilityData?.pinned_encounter_id ?? undefined;
    openModal(MODAL_TYPES.PAYMENT_REQUEST, {
      patientId,
      patientName,
      encounterId: resolvedEncounterId,
      appointmentId: eligibilityData?.pinned_appointment_id ?? undefined,
      amount,
      source: 'eligibility_popover',
    });
  }, [missingBillingNpi, navigate, openModal, eligibilityData, patientId, patientName, encounterId]);

  if (!hasCoverage) {
    return {
      primary: { label: 'Add coverage', onClick: openAddCoverage },
    };
  }

  switch (readiness) {
    case 'collect':
      return {
        primary: {
          label: missingBillingNpi ? 'Finish billing setup' : 'Take payment',
          onClick: openTakePayment,
        },
      };
    case 'blocked':
      return {
        primary: { label: 'Add coverage', onClick: openAddCoverage },
      };
    case 'verified':
    case 'collected':
    case 'attention':
    case 'info':
    case null:
    default:
      return null;
  }
}
